import { z } from 'zod';
import { zDbId, zGuid, zLuxonDateTime, zNullishDefault } from '~/plugins/zod/utils';
import { API_DATE_FORMAT } from '~/constants/_constants';
import { OrderAttachmentCategory, OrderLineState, OrderState } from '~/enums/_types';
import { CategoryItemDetail, PartnerSimpleSchema, Price, PriceSchema } from '~/api/links/_types';
import { ContactSimpleSchema } from '../contacts/_types';
import { AddressSimpleSchema } from '../partners/_types';
import { InvoiceSimpleSchema } from '../invoices/_types';
import { DeliveryListSimpleSchema } from '../deliveryLists/_types';
import { CategoryItemDetailSchema } from '../links/_types';
import { NomenclatureConfigSnapshotInfoSchema } from '../items/_types';

export const OrderInfoSchema = z.object({
    Id: zDbId(),
    ExternalId: z.number().nullable(),
    Status: z.nativeEnum(OrderState),
    DocumentNumber: z.number().nullable(),
    ReferenceNumber: z.string().nullable(),
    Partner: PartnerSimpleSchema.nullable(),
    Contact: ContactSimpleSchema.nullable(),
    CreatedAt: zLuxonDateTime({ format: API_DATE_FORMAT }),
    RequiredOrShipAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Price: PriceSchema.nullable(),
});

export const OrderDraftLineConfigSchema = z.object({
    RuleId: zDbId(),
    OptionId: zDbId(),
});

export const OrderDraftLineInfoSchema = z.object({
    Id: zDbId(),
    Item: CategoryItemDetailSchema,
    Quantity: z.number(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Description: z.string().nullable(),
    Nomenclature: z.string().nullable(),
    PriceCatalogue: PriceSchema.nullable(),
    PriceDistributor: PriceSchema.nullable(),
    Configuration: zNullishDefault(OrderDraftLineConfigSchema.array(), []),
});

export const OrderDraftDetailSchema = z.object({
    Id: zDbId(),
    Partner: PartnerSimpleSchema,
    Contact: ContactSimpleSchema.nullable(),
    ReferenceNumber: z.string().nullable(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    AddressBilling: AddressSimpleSchema.nullable(),
    AddressShipping: AddressSimpleSchema.nullable(),
    HasOtherAddressShipping: z.boolean(),
    PriceCatalogue: PriceSchema.nullable(),
    PriceDistributor: PriceSchema.nullable(),
    Note: z.string().nullable(),
    Lines: zNullishDefault(OrderDraftLineInfoSchema.array(), []),
});

export const OrderDetailLineInfoSchema = z.object({
    Id: zDbId(),
    // @plachtova: tato hodnota muze byt null, protoze SAP nam ji nemusi dodat
    Item: CategoryItemDetailSchema.nullable(),
    ItemCode: z.string(),
    Status: z.nativeEnum(OrderLineState),
    Quantity: z.number(),
    ShipAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    RequiredOrShipAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    Description: z.string(),
    Nomenclature: z.string().nullable(),
    Configuration: NomenclatureConfigSnapshotInfoSchema.array(),
    PriceCatalogue: PriceSchema.nullable(),
    PriceDistributor: PriceSchema.nullable(),
});

export const OrderAttachmentInfoSchema = z.object({
    Id: zGuid(),
    Url: z.string(),
    Category: z.nativeEnum(OrderAttachmentCategory),
    CreatedAt: zLuxonDateTime({ iso: true }),
});

export const OrderDetailSchema = z.object({
    Id: zDbId(),
    ExternalId: z.number().nullable(),
    Status: z.nativeEnum(OrderState),
    DocumentNumber: z.number().nullable(),
    ReferenceNumber: z.string().nullable(),
    Partner: PartnerSimpleSchema.nullable(),
    Contact: ContactSimpleSchema.nullable(),
    CreatedAt: zLuxonDateTime({ format: API_DATE_FORMAT }),
    ShipAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    RequiredOrShipAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullable(),
    AddressBilling: AddressSimpleSchema.nullable(),
    AddressShipping: AddressSimpleSchema.nullable(),
    HasOtherAddressShipping: z.boolean(),
    Note: z.string().nullable(),
    Invoices: InvoiceSimpleSchema.array(),
    Deliveries: DeliveryListSimpleSchema.array(),
    PriceCatalogue: PriceSchema.nullable(),
    PriceDistributor: PriceSchema.nullable(),
    Lines: zNullishDefault(OrderDetailLineInfoSchema.array(), []),
    Attachments: OrderAttachmentInfoSchema.array(),
    Can: z.object({
        RevertToDraft: z.boolean(),
    }),
});

export const OrderItemConfigFormDataSchema = z.object({
    RuleId: z.number(),
    OptionId: z.number(),
});
export const OrderLineFormDataSchema = z.object({
    // @plachtova: bud to bude db id z BE nebo pokud jeste neexituje na BE tak bude mit prefix `N` a bude se vytvaret na FE
    // pri ulozeni draftu se FE id prekonvertuje na BE id
    Id: z.string(),
    // pouze pro FE, custom schema bude skipovat validaci pro tuto property
    Item: z.custom<CategoryItemDetail>(),
    Quantity: z.number().min(1),
    RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullish(),
    Configuration: OrderItemConfigFormDataSchema.array(),
    CategoryId: z.number().nullish(),
    Nomenclature: z.custom<string | null>(),
    PriceCatalogue: z.custom<Price | null>(),
    PriceDistributor: z.custom<Price | null>(),
});

export const OrderFormDataSchema = z.object({
    Order: z.object({
        Id: z.number().nullish(),
        PartnerId: PartnerSimpleSchema,
        ContactId: ContactSimpleSchema.nullish(),
        ReferenceNumber: z.string().nullish(),
        RequiredAt: zLuxonDateTime({ format: API_DATE_FORMAT }).nullish(),
        AddressBillingId: AddressSimpleSchema.nullish(),
        AddressShippingId: AddressSimpleSchema.nullish(),
        HasOtherAddressShipping: z.boolean(),
        Note: z.string().nullish(),
    }),
    Lines: OrderLineFormDataSchema.array(),
});

export const FetchOrdersResponseSchema = OrderInfoSchema.array();

export type OrderInfo = z.infer<typeof OrderInfoSchema>;
export type OrderDraftLineInfo = z.infer<typeof OrderDraftLineInfoSchema>;
export type OrderDraftDetail = z.infer<typeof OrderDraftDetailSchema>;
export type OrderDetailLineInfo = z.infer<typeof OrderDetailLineInfoSchema>;
export type OrderDetail = z.infer<typeof OrderDetailSchema>;
export type OrderLineFormData = z.infer<typeof OrderLineFormDataSchema>;
export type OrderFormData = z.infer<typeof OrderFormDataSchema>;
export type FetchOrdersResponse = z.infer<typeof FetchOrdersResponseSchema>;
